import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api, type ApiError, type CustomerHistoryResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Full customer history (G-045) on the company record.
 *
 * The balance comes first, per currency, with the overdue part beside it -
 * it is the figure the dunning ladder acts on. A viewer who may not see
 * invoices gets no balance line at all rather than an empty one, which would
 * read as "owes nothing".
 *
 * Anything withheld is said in a sentence under the list: "2 invoices you do
 * not have access to". A history with a kind silently missing is the one
 * thing this panel must not look like.
 */

type History = CustomerHistoryResult['history'];
type Kind = History['items'][number]['kind'];
const KINDS: Kind[] = ['deal', 'quote', 'order', 'invoice', 'payment', 'ticket', 'campaign', 'subscription'];

export function CustomerHistoryPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const enabled = useEntitlement('F-028');
  const [only, setOnly] = useState<Kind | null>(null);

  const history = useInfiniteQuery<CustomerHistoryResult, ApiError>({
    queryKey: ['customer-history', accountId, only],
    queryFn: ({ pageParam }) =>
      api.customerHistory({
        path: { accountId },
        query: {
          limit: 25,
          ...(only ? { kinds: only } : {}),
          ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.history.nextCursor ?? undefined,
    enabled: enabled.enabled,
  });

  if (!enabled.enabled || history.isPending) return null;
  if (history.isError) {
    return (
      <Panel data-region="customer-history">
        <p className="text-dense text-danger-text">{errorText(history.error)}</p>
      </Panel>
    );
  }

  const first = history.data.pages[0]!.history;
  const items = history.data.pages.flatMap((page) => page.history.items);

  return (
    <Panel data-region="customer-history">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('customerHistory.title')}</SectionLabel>

        {first.balance ? (
          <div className="flex flex-wrap gap-4" data-history-balance>
            {first.balance.length === 0 ? (
              <span className="text-dense text-ink-meta" data-history-balance-clear>
                {t('customerHistory.balance.clear')}
              </span>
            ) : (
              first.balance.map((entry) => (
                <span key={entry.currency} className="flex flex-col" data-history-balance-currency={entry.currency}>
                  <span className="text-title text-ink">
                    {entry.outstanding} {entry.currency}
                  </span>
                  <span className="text-meta text-ink-meta">
                    {t('customerHistory.balance.detail', {
                      values: { overdue: `${entry.overdue} ${entry.currency}`, invoices: String(entry.invoices) },
                    })}
                  </span>
                </span>
              ))
            )}
          </div>
        ) : null}

        <div role="group" aria-label={t('customerHistory.filter')} className="flex flex-wrap gap-1">
          <Button intent={only === null ? 'primary' : 'secondary'} size="small" data-history-filter="all"
            onClick={() => setOnly(null)}>
            {t('customerHistory.kind.all')}
          </Button>
          {KINDS.map((kind) => (
            <Button key={kind} intent={only === kind ? 'primary' : 'secondary'} size="small"
              data-history-filter={kind} onClick={() => setOnly(kind)}>
              {t(`customerHistory.kind.${kind}`)}
            </Button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-dense text-ink-meta" data-history-empty>{t('customerHistory.empty')}</p>
        ) : (
          <ol className="flex flex-col divide-y divide-line">
            {items.map((item) => (
              <li key={`${item.kind}-${item.id}`} data-history-item={item.kind}
                className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-dense">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="rounded-chip bg-sunken px-2 text-meta text-ink-600">
                    {t(`customerHistory.kind.${item.kind}`)}
                  </span>
                  {item.href ? (
                    <Link to={item.href} className="text-ink underline-offset-2 hover:underline">{item.title}</Link>
                  ) : (
                    <span className="text-ink">{item.title}</span>
                  )}
                  {item.status ? <span className="text-meta text-ink-meta">{item.status}</span> : null}
                </span>
                <span className="flex gap-3 text-meta text-ink-meta">
                  {item.amount ? <span>{item.amount} {item.currency ?? ''}</span> : null}
                  <time dateTime={item.occurredAt}>{item.occurredAt.slice(0, 10)}</time>
                </span>
              </li>
            ))}
          </ol>
        )}

        {/* Said, not omitted: a missing kind must not read as "none". */}
        {first.withheld.length > 0 ? (
          <ul className="text-meta text-ink-meta" data-history-withheld>
            {first.withheld.map((entry) => (
              <li key={entry.kind} data-history-withheld-kind={entry.kind}>
                {t('customerHistory.withheld', {
                  values: { count: String(entry.count), kind: t(`customerHistory.kind.${entry.kind}`).toLowerCase() },
                })}
              </li>
            ))}
          </ul>
        ) : null}

        {history.hasNextPage ? (
          <Button intent="secondary" size="small" data-history-more
            onClick={() => void history.fetchNextPage()} disabled={history.isFetchingNextPage}>
            {t('customerHistory.more')}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
